import { uploadFileToS3 } from './s3Utils';
import { buildCloudFrontUrl, getExtension } from './pathUtils';

type VectorType = 'base' | 'vector' | 'vector-svg' | 'vector-pdf';

export const getVectorTypeByFileExtension = (extension: string): VectorType => {
    const uploadTypes: Record<string, VectorType> = {
        eps: 'vector',
        svg: 'vector-svg',
        pdf: 'vector-pdf'
    };
    return uploadTypes[extension];
};

export const uploadVectorToS3 = async (
    vector: File,
    includeDimensions: boolean,
    generateThumb: boolean,
    vectorType?: VectorType,
    progressCallback?: (type: 'upload' | 'process', progress: number) => void
) => {
    const type = vectorType || getVectorTypeByFileExtension(getExtension(vector.name));
    // Get signed URL
    const response = await getVectorUploadS3SignedUrl(type, vector.name || '');

    // Upload to S3
    await uploadFileToS3(response.SignedUrl, response.Mime, vector, undefined, (progress) =>
        progressCallback?.('upload', progress)
    );

    // Validate the vector file
    progressCallback?.('process', 0);
    const validationResponse = await validateVectorFile(type, response.Name, includeDimensions, generateThumb);
    progressCallback?.('process', 100);

    return validationResponse;
};

export const getSvgStringFromPath = async (svgPath: string) => {
    const fetchHeaders = new Headers();
    fetchHeaders.append('pragma', 'no-cache');
    fetchHeaders.append('cache-control', 'no-cache');
    const fetchConfig: RequestInit = {
        method: 'GET',
        mode: 'cors',
        headers: fetchHeaders
    };

    // Try fetching locally
    const response = await fetch(svgPath, fetchConfig);

    if (response.ok) return response.text();

    if (response.status !== 404) {
        console.error(`Unexpected error from ${svgPath}: ${response.status}`);
        throw new Error('Unexpected error');
    }

    // Fallback to CloudFront URL
    const cloudFrontUrl = buildCloudFrontUrl(svgPath);
    const fallbackResponse = await fetch(cloudFrontUrl, fetchConfig);
    if (fallbackResponse.ok) return fallbackResponse.text();

    console.error(`CloudFront fallback failed: ${fallbackResponse.status}`);
    throw new Error('Could not fetch SVG from any source.');
};

import api from '@/api/baseApi';
import type { SignedUrlResponse } from '@/api/typings/image';

export interface ValidatedVector {
    EPS: string;
    SVG: string;
    Width: number;
    Height: number;
    ThumbnailPath: string;
    id: string;
}

export const getVectorUploadS3SignedUrl = async (type: string, filename: string): Promise<SignedUrlResponse> =>
    api
        .get<SignedUrlResponse>('/Vector/VectorUploadSignedUrl', {
            params: { type, filename }
        })
        .then(({ data }) => data);

export const validateVectorFile = async (
    type: string,
    filename: string,
    includeDimensions: boolean,
    generateThumb: boolean
): Promise<ValidatedVector> =>
    api
        .get<ValidatedVector>(`/Vector/Validate/${encodeURIComponent(filename)}`, {
            params: {
                type,
                filename,
                includeDimensions,
                generateThumb
            }
        })
        .then(({ data }) => data);
